<script lang="ts">
  import type { Snippet } from 'svelte';
  import Button from '../ui/Button.svelte';
  import ThemeIcon from '../themes/ThemeIcon.svelte';
  import type { PlayerView } from '../../shared/protocol';

  interface Props {
    players: readonly Readonly<PlayerView>[];
    meId: string;
    /** Only the role holder removes players; a stand-in host cannot. */
    canKick: boolean;
    kickPlayer: (id: string) => void;
    /** Live lobbies dim and label disconnected or unready players; turn-taking lobbies do not. */
    presence?: boolean;
    /** Per-player detail after the name, such as turn progress. */
    detail?: Snippet<[Readonly<PlayerView>]>;
  }
  let { players, meId, canKick, kickPlayer, presence = true, detail }: Props = $props();
</script>

<ul class="players" aria-label="Participants">
  {#each players as player (player.id)}
    <li class:off={presence && !player.connected} class:you={player.id === meId} class="player">
      <span class="av">{player.avatar}</span>
      <span class="nm"
        ><span>{player.name}{player.id === meId ? ' (you)' : ''}</span>{#if player.host}<span
            class="host-label">host</span
          >{/if}{#if presence}{#if player.ready === false && !player.host}<span class="status">Not Ready</span
            >{:else if !player.connected}<span class="status">away</span>{/if}{/if}</span
      >
      {@render detail?.(player)}
      {#if player.total > 0}<span class="tot">{player.total} pts</span>{/if}
      {#if canKick && player.id !== meId}<Button
          variant="destructive"
          size="small"
          class="kick"
          title="Remove player"
          aria-label={`Remove ${player.name}`}
          onclick={() => kickPlayer(player.id)}><ThemeIcon name="close" /></Button
        >{/if}
    </li>
  {/each}
</ul>

<style>
  .host-label {
    font-size: 11px;
    color: var(--theme-muted-text);
  }
  .players {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .player {
    display: flex;
    align-items: center;
    gap: 10px;
    background: var(--theme-surface);
    border: var(--theme-card-border);
    border-radius: var(--theme-chip-radius);
    padding: 8px 12px;
    animation: var(--theme-enter-animation);
  }
  .player .av {
    font-size: 26px;
    width: 34px;
    text-align: center;
  }
  .player .nm {
    flex: 1;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }
  .player .nm span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .player .tot {
    color: var(--theme-muted-text);
    font-size: 13px;
  }
  .player.off {
    opacity: 0.55;
  }
  .player .nm .status {
    font-size: 11px;
    color: var(--theme-muted-text);
    font-weight: 600;
    background: var(--theme-surface);
    border-radius: 999px;
    padding: 1px 6px;
  }
  .player :global(.btn.kick) {
    padding: 4px;
  }
  .player.you {
    border-style: double;
    border-color: var(--theme-accent);
    background: var(--theme-highlight);
  }
</style>
